/**
 * Deploy instance wizard — agent + optional industry pack + version pin + name.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { listAgents, listCompatibleIndustryPacks, listVersions } from '@/services/aiStudioAgentService';
import { STUDIO_BINDING_STATUSES } from '@/types/aiStudioAgent';
import type {
  AiStudioAgent,
  AiStudioAgentVersion,
  AiStudioIndustryPack,
  CreateInstanceRequest,
  InstanceDeployPatch,
  StudioBindingStatus,
} from '@/types/aiStudioAgent';
import {
  InstanceHierarchyScopeFields,
  type InstanceHierarchyScope,
} from '@/components/ai-studio/hierarchy';

interface CreateInstanceFormProps {
  submitting: boolean;
  initialAgentId?: string;
  embedded?: boolean;
  /** Apply deploy-assist suggestion when parent sets this. */
  suggestedPatch?: InstanceDeployPatch | null;
  onSuggestionApplied?: () => void;
  onDraftSnapshot?: (draft: InstanceDeployPatch & { agentId: string; pinnedVersionId: string }) => void;
  /** Translation key for submit button (default: aiStudio.common.create). */
  submitLabelKey?: string;
  onSubmit: (request: CreateInstanceRequest) => void;
  onCancel: () => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';

export const CreateInstanceForm: React.FC<CreateInstanceFormProps> = ({
  submitting,
  initialAgentId,
  embedded = false,
  suggestedPatch,
  onSuggestionApplied,
  onDraftSnapshot,
  submitLabelKey = 'aiStudio.common.create',
  onSubmit,
  onCancel,
}) => {
  const { t } = useLanguage();
  const [agents, setAgents] = useState<AiStudioAgent[]>([]);
  const [versions, setVersions] = useState<AiStudioAgentVersion[]>([]);
  const [packs, setPacks] = useState<AiStudioIndustryPack[]>([]);
  const [packRequired, setPackRequired] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [agentId, setAgentId] = useState(initialAgentId ?? '');
  const [versionId, setVersionId] = useState('');
  const [packId, setPackId] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [status, setStatus] = useState<StudioBindingStatus>('active');
  const [hierarchyScope, setHierarchyScope] = useState<InstanceHierarchyScope>({
    businessHierarchyId: '',
    functionalHierarchyId: '',
    geographicHierarchyId: '',
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!suggestedPatch) return;
    if (suggestedPatch.instanceName) setInstanceName(suggestedPatch.instanceName);
    if (suggestedPatch.industryPackId !== undefined) {
      setPackId(suggestedPatch.industryPackId ?? '');
    }
    setHierarchyScope((current) => ({
      businessHierarchyId:
        suggestedPatch.businessHierarchyId ?? current.businessHierarchyId,
      functionalHierarchyId:
        suggestedPatch.functionalHierarchyId ?? current.functionalHierarchyId,
      geographicHierarchyId:
        suggestedPatch.geographicHierarchyId ?? current.geographicHierarchyId,
    }));
    onSuggestionApplied?.();
  }, [suggestedPatch, onSuggestionApplied]);

  useEffect(() => {
    if (!onDraftSnapshot || !agentId || !versionId) return;
    onDraftSnapshot({
      agentId,
      pinnedVersionId: versionId,
      instanceName,
      industryPackId: packId || null,
      businessHierarchyId: hierarchyScope.businessHierarchyId || null,
      functionalHierarchyId: hierarchyScope.functionalHierarchyId || null,
      geographicHierarchyId: hierarchyScope.geographicHierarchyId || null,
    });
  }, [
    agentId,
    versionId,
    instanceName,
    packId,
    hierarchyScope,
    onDraftSnapshot,
  ]);

  useEffect(() => {
    if (initialAgentId) {
      setAgentId(initialAgentId);
    }
  }, [initialAgentId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    Promise.all([listAgents({ status: 'published', audience: 'catalog' })])
      .then(([agentRows]) => {
        if (cancelled) return;
        setAgents(agentRows);
      })
      .catch(() => {
        if (!cancelled) setValidationError(t('aiStudio.instances.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!agentId) {
      setVersions([]);
      setVersionId('');
      setPacks([]);
      setPackId('');
      setPackRequired(false);
      return;
    }
    let cancelled = false;
    Promise.all([listVersions(agentId), listCompatibleIndustryPacks(agentId)])
      .then(([rows, compatiblePackRows]) => {
        if (cancelled) return;
        const published = rows.filter((row) => row.lifecycleStatus === 'published');
        setVersions(published);
        const compatible = compatiblePackRows.filter((pack) => pack.lifecycleStatus === 'published');
        setPacks(compatible);
        setPackRequired(compatible.length > 0);
        const defaultVersion = published[0];
        setVersionId(defaultVersion?.id ?? '');
        const defaultPackId =
          defaultVersion?.industryPackId &&
          compatible.some((pack) => pack.id === defaultVersion.industryPackId)
            ? defaultVersion.industryPackId
            : compatible.length === 1
              ? compatible[0]?.id ?? ''
              : '';
        setPackId(defaultPackId);
      })
      .catch(() => {
        if (!cancelled) {
          setVersions([]);
          setVersionId('');
          setPacks([]);
          setPackRequired(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    if (!versionId || packs.length === 0) return;
    const selectedVersion = versions.find((row) => row.id === versionId);
    if (
      selectedVersion?.industryPackId &&
      packs.some((pack) => pack.id === selectedVersion.industryPackId)
    ) {
      setPackId(selectedVersion.industryPackId);
    }
  }, [versionId, versions, packs]);

  const publishedPacks = useMemo(() => packs, [packs]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!agentId || !versionId || !instanceName.trim()) {
      setValidationError(t('aiStudio.instances.form.requiredFields'));
      return;
    }
    if (packRequired && !packId) {
      setValidationError(t('aiStudio.instances.form.packSelectRequired'));
      return;
    }
    setValidationError(null);
    onSubmit({
      pinnedVersionId: versionId,
      instanceName: instanceName.trim(),
      industryPackId: packId || undefined,
      status,
      businessHierarchyId: hierarchyScope.businessHierarchyId || null,
      functionalHierarchyId: hierarchyScope.functionalHierarchyId || null,
      geographicHierarchyId: hierarchyScope.geographicHierarchyId || null,
    });
  };

  const formBody = (
    <>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={LABEL_CLASS} htmlFor="instance-agent">
            {t('aiStudio.instances.form.agent')}
          </label>
          <select
            id="instance-agent"
            className={FIELD_CLASS}
            value={agentId}
            disabled={loadingOptions || submitting}
            onChange={(event) => setAgentId(event.target.value)}
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name} ({agent.code})
              </option>
            ))}
          </select>
          {!loadingOptions && agents.length === 0 && (
            <p className="mt-1 text-base text-[var(--color-error)]">
              {t('aiStudio.instances.form.noAgents')}
            </p>
          )}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="instance-version">
            {t('aiStudio.instances.form.version')}
          </label>
          <select
            id="instance-version"
            className={FIELD_CLASS}
            value={versionId}
            disabled={!agentId || submitting}
            onChange={(event) => setVersionId(event.target.value)}
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {versions.map((version) => (
              <option key={version.id} value={version.id}>
                {t('aiStudio.instances.form.versionOption', { number: version.versionNumber })}
              </option>
            ))}
          </select>
          {agentId && versions.length === 0 && (
            <p className="mt-1 text-base text-[var(--color-error)]">
              {t('aiStudio.instances.form.noVersions')}
            </p>
          )}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="instance-pack">
            {packRequired
              ? t('aiStudio.instances.form.packRequired')
              : t('aiStudio.instances.form.packOptional')}
          </label>
          <select
            id="instance-pack"
            className={FIELD_CLASS}
            value={packId}
            disabled={submitting || (packRequired && publishedPacks.length === 0)}
            required={packRequired}
            onChange={(event) => setPackId(event.target.value)}
          >
            <option value="">
              {packRequired ? t('aiStudio.instances.form.packSelectRequired') : t('aiStudio.common.none')}
            </option>
            {publishedPacks.map((pack) => (
              <option key={pack.id} value={pack.id}>
                {pack.name} ({pack.code})
              </option>
            ))}
          </select>
          {packRequired && (
            <p className="mt-1 text-sm text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.instances.form.packRequiredHint')}
            </p>
          )}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="instance-name">
            {t('aiStudio.instances.form.instanceName')}
          </label>
          <input
            id="instance-name"
            className={FIELD_CLASS}
            value={instanceName}
            disabled={submitting}
            autoComplete="off"
            onChange={(event) => setInstanceName(event.target.value)}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="instance-status">
            {t('aiStudio.instances.form.status')}
          </label>
          <select
            id="instance-status"
            className={FIELD_CLASS}
            value={status}
            disabled={submitting}
            onChange={(event) => setStatus(event.target.value as StudioBindingStatus)}
          >
            {STUDIO_BINDING_STATUSES.map((option) => (
              <option key={option} value={option}>
                {t(`aiStudio.bindingStatus.${option}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3">
        <InstanceHierarchyScopeFields
          value={hierarchyScope}
          disabled={submitting}
          onChange={setHierarchyScope}
        />
      </div>

      {validationError && (
        <p role="alert" className="mt-3 text-base text-[var(--color-error)]">
          {validationError}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={submitting || loadingOptions}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
        >
          {submitting ? t('aiStudio.common.saving') : t(submitLabelKey)}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
        >
          {t('aiStudio.common.cancel')}
        </button>
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {formBody}
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4"
    >
      <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.instances.form.title')}
      </h2>
      {formBody}
    </form>
  );
};
